import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationScrollService implements OnDestroy {
  private readonly subscription: Subscription;
  private previousPath: string | null = null;
  private pageChanged = true;
  private navigationId = 0;
  private cancelPendingScroll: (() => void) | undefined;

  constructor(
    router: Router,
    private readonly scroller: ViewportScroller,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly zone: NgZone
  ) {
    scroller.setHistoryScrollRestoration('manual');
    scroller.setOffset(() => [
      0, (document.querySelector('.app-header')?.getBoundingClientRect().height ?? 0) + 16
    ]);
    this.subscription = router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationId = event.id;
        this.cancelPendingScroll?.();
      } else if (event instanceof NavigationEnd) {
        const path = event.urlAfterRedirects.split(/[?#]/)[0];
        this.pageChanged = path !== this.previousPath;
        this.previousPath = path;
      } else if (event instanceof Scroll && event.routerEvent.id === this.navigationId) {
        if (event.position) {
          this.restorePosition(event.position);
        } else if (event.anchor) {
          this.scrollToAnchor(event.anchor);
        } else if (this.pageChanged) {
          this.scrollToPositionImmediately([0, 0]);
        }
      }
    });
  }

  private restorePosition(position: [number, number]): void {
    this.scrollToPositionImmediately(position);
    const canRestore = () => {
      const element = this.document.scrollingElement ?? this.document.documentElement;
      return element.scrollWidth - element.clientWidth >= position[0]
        && element.scrollHeight - element.clientHeight >= position[1];
    };
    if (canRestore()) return;

    // The loading shell may clamp the first attempt. Retry when data-backed
    // content or images make the document large enough for the saved position.
    this.zone.runOutsideAngular(() => this.waitForLayout(
      canRestore, () => this.scrollToPositionImmediately(position)
    ));
  }

  private scrollToPositionImmediately(position: [number, number]): void {
    // Angular 17's scroller uses window.scrollTo(x, y), which inherits
    // Bootstrap's smooth scrolling. Restoration must finish before we sample
    // its position and start listening for user-driven scrolling.
    const style = this.document.documentElement.style;
    const previous = style.getPropertyValue('scroll-behavior');
    const priority = style.getPropertyPriority('scroll-behavior');
    style.setProperty('scroll-behavior', 'auto', 'important');
    try {
      this.scroller.scrollToPosition(position);
    } finally {
      if (previous) style.setProperty('scroll-behavior', previous, priority);
      else style.removeProperty('scroll-behavior');
    }
  }

  private scrollToAnchor(anchor: string): void {
    const hasTarget = () => !!(
      this.document.getElementById(anchor) || this.document.getElementsByName(anchor).length
    );
    if (hasTarget()) {
      this.scroller.scrollToAnchor(anchor);
      return;
    }

    // DOM observation must not trigger Angular change detection: its attribute
    // writes can otherwise feed back into the observer indefinitely.
    this.zone.runOutsideAngular(() => this.waitForLayout(
      hasTarget, () => this.scroller.scrollToAnchor(anchor)
    ));
  }

  private waitForLayout(isReady: () => boolean, apply: () => void): void {
    const view = this.document.defaultView;
    if (!view) return;
    const initialPosition = this.scroller.getScrollPosition();
    let frame: number | undefined;
    const check = () => {
      frame = undefined;
      if (isReady()) {
        cancel();
        apply();
      }
    };
    const scheduleCheck = () => {
      if (frame === undefined) frame = view.requestAnimationFrame(check);
    };
    const observer = new view.MutationObserver(scheduleCheck);
    const resizeObserver = new view.ResizeObserver(scheduleCheck);
    const onScroll = () => {
      const current = this.scroller.getScrollPosition();
      // Ignore the queued scroll event from our initial, possibly clamped,
      // restoration. A different position means the user has taken control,
      // including native scrollbar movement that emits no pointer event.
      if (current[0] !== initialPosition[0] || current[1] !== initialPosition[1]) cancel();
    };
    const interactionEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const cancel = () => {
      observer.disconnect();
      resizeObserver.disconnect();
      view.clearTimeout(timeout);
      if (frame !== undefined) view.cancelAnimationFrame(frame);
      view.removeEventListener('scroll', onScroll, true);
      view.removeEventListener('resize', scheduleCheck);
      for (const event of interactionEvents) {
        this.document.removeEventListener(event, cancel, true);
      }
      this.cancelPendingScroll = undefined;
    };
    const timeout = view.setTimeout(cancel, 15000);
    this.cancelPendingScroll = cancel;
    for (const event of interactionEvents) {
      this.document.addEventListener(event, cancel, { passive: true, capture: true });
    }
    view.addEventListener('scroll', onScroll, { passive: true, capture: true });
    view.addEventListener('resize', scheduleCheck, { passive: true });
    observer.observe(this.document.body, { childList: true, subtree: true, attributes: true });
    resizeObserver.observe(this.document.body);
    resizeObserver.observe(this.document.documentElement);
    scheduleCheck();
  }

  ngOnDestroy(): void {
    this.cancelPendingScroll?.();
    this.subscription.unsubscribe();
  }
}
